import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useStore, useIsLoaded } from '../../../../store';
import { ApplicationCard } from '../../../../components/applications';
import { CreateApplicationDialog } from '../../../../components/CreateApplicationDialog';
import { AddVersionDialog } from '../../../../components/AddVersionDialog';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ChangeThumbnailDialog } from '../../../../components/applications/ChangeThumbnailDialog';
import { MoveApplicationDialog } from '../../../../components/applications/MoveApplicationDialog';
import { ApplicationGridSkeleton } from '../../../../components/skeletons';
import { ListControls } from '../../../../components/ListControls';
import { useListControls } from '../../../../hooks/useListControls';
import { send } from '../../../../ws';
import type { Application } from '@interlucent/organization-protocol';
import { useAppContext } from '../../../../contexts/AppContext';
import { createPreviewShare } from '../../../../utils/createPreviewShare';
import { storePreviewContext } from '../../../preview.$';
import { waitForReady } from '../../../../ws';
import { logger } from '../../../../log';
import styles from '../../../../components/ProjectsView.module.css';

const log = logger('applications-tab');

export const Route = createFileRoute('/projects/$projectId/applications/')({
  component: ApplicationsTab,
});

function ApplicationsTab() {
  const { projectId } = useParams({ from: '/projects/$projectId/applications/' });
  const { handleUploadStarted, handleCancelUpload, handlePauseUpload, handleResumeUpload } = useAppContext();
  const isConnected = useStore(s => s.connectionState === 'connected');
  const uploadProgress = useStore(s => s.uploadProgress);
  const applications = useStore(s => s.applications);
  const versions = useStore(s => s.versions);
  const profiles = useStore(s => s.profiles);
  const mappings = useStore(s => s.mappings);
  const appPoolCoverage = useStore(s => s.appPoolCoverage);
  const organization = useStore(s => s.organization);

  const [showCreateApplication, setShowCreateApplication] = useState(false);
  const [addVersionApp, setAddVersionApp] = useState<Application | null>(null);
  const [replaceVersion, setReplaceVersion] = useState<{ app: Application; versionId: string } | null>(null);
  const [thumbnailApp, setThumbnailApp] = useState<Application | null>(null);
  const [moveApp, setMoveApp] = useState<Application | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    requireConfirmation?: boolean;
    confirmationPhrase?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const versionQuota = organization?.planTier?.quotaMaxVersionsPerApp ?? 5;
  const projectsLoaded = useIsLoaded('projects');

  const handleDeleteVersion = (versionId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Version',
      message: 'Delete this version?',
      onConfirm: () => {
        if (isConnected) {
          send('version.delete', { versionId });
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleDeleteApplication = (app: Application) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Application',
      message: `This will permanently delete "${app.name}" and all its versions.`,
      requireConfirmation: true,
      confirmationPhrase: app.name,
      onConfirm: () => {
        if (isConnected) {
          send('application.delete', { appId: app.id });
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleSetDefaultVersion = (appId: string, versionId: string) => {
    if (isConnected) {
      send('application.setDefaultVersion', { appId, versionId });
    }
  };

  const navigate = useNavigate();

  const handlePreview = useCallback(async (app: Application) => {
    const defaultVersion = Object.values(versions).find(
      v => v.appId === app.id && v.id === app.defaultVersionId
    );
    if (!defaultVersion) return;

    try {
      await waitForReady();
      const { id: shareId } = await createPreviewShare({
        projectId,
        appId: app.id,
        versionId: defaultVersion.id,
      });
      storePreviewContext({
        title: `${app.name} - ${defaultVersion.id}`,
        projectId,
        appId: app.id,
        versionId: defaultVersion.id,
      });
      navigate({ to: '/preview/$', params: { _splat: shareId } });
    } catch (err) {
      log.error('failed to create preview share', err);
    }
  }, [projectId, versions, navigate]);

  const projectApplications = useMemo(
    () => Object.values(applications).filter(app => app.projectId === projectId),
    [applications, projectId],
  );

  // Pre-compute searchable haystack per app, including its versions' fields.
  const appHaystacks = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of projectApplications) {
      const appVersions = Object.values(versions).filter(v => v.appId === app.id);
      const versionParts: string[] = [];
      for (const v of appVersions) {
        versionParts.push(
          v.id ?? '',
          v.executablePath ?? '',
          v.cmdArgs ?? '',
          v.environmentVariables ?? '',
          v.notes ?? '',
          v.description ?? '',
        );
      }
      const parts = [
        app.name,
        app.description ?? '',
        app.appSlug ?? '',
        app.id,
        ...versionParts,
      ];
      map.set(app.id, parts.filter(Boolean).join(' '));
    }
    return map;
  }, [projectApplications, versions]);

  const { search, setSearch, sort, setSort, visible: visibleApplications } = useListControls(projectApplications, {
    getSearchHaystack: (app) => appHaystacks.get(app.id) ?? app.name,
    getName: (app) => app.name,
    getCreatedAt: (app) => app.createdAt,
    getUpdatedAt: (app) => app.updatedAt,
  });

  // Filter uploads for this project
  const projectUploadProgress = useMemo(() =>
    Object.fromEntries(
      Object.entries(uploadProgress).filter(([, progress]) => progress.projectId === projectId)
    ),
    [uploadProgress, projectId]
  );

  return (
    <div>
      {/* Toolbar */}
      <div className={styles.tabToolbar}>
        <ListControls
          search={search}
          onSearchChange={setSearch}
          sort={sort}
          onSortChange={setSort}
          searchPlaceholder="Search applications…"
        />
        <button
          onClick={() => setShowCreateApplication(true)}
          disabled={!isConnected}
          className={styles.createButton}
        >
          Create Application
        </button>
      </div>

      {/* Applications List */}
      {!projectsLoaded ? (
        <ApplicationGridSkeleton count={3} />
      ) : projectApplications.length === 0 ? (
        <div className={styles.emptyState}>
          No applications yet. Upload a version to create one.
        </div>
      ) : visibleApplications.length === 0 ? (
        <div className={styles.emptyState}>
          No applications match your search.
        </div>
      ) : (
        <div className={styles.applicationList}>
          {visibleApplications.map(app => {
            const appVersions = Object.values(versions).filter(v => v.appId === app.id);

            // Resolver-correct pool coverage from the WS snapshot's
            // `coveringPools` slice (populated from pool_materialized_entries).
            // Falls back to the legacy DPA-derivation while coverage for this
            // app hasn't streamed in yet — same pattern as $appId.tsx.
            const coverage = appPoolCoverage[app.id] ?? null;
            let deployments: Array<{ id: string; name: string; active: boolean }>;
            if (coverage !== null) {
              const byPool = new Map<string, { id: string; name: string; active: boolean }>();
              for (const c of coverage) {
                if (!byPool.has(c.poolId)) {
                  byPool.set(c.poolId, { id: c.poolId, name: c.poolName, active: c.active });
                }
              }
              deployments = [...byPool.values()].sort((a, b) => a.name.localeCompare(b.name));
            } else {
              const appMappings = Object.values(mappings).filter(m => m.appId === app.id);
              deployments = appMappings
                .map(m => profiles[m.deploymentProfileId])
                .filter(Boolean)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(p => ({ id: p.id, name: p.name, active: Boolean(p.active) }));
            }

            // Filter uploads for this application
            const appUploads = Object.entries(projectUploadProgress).filter(
              ([, progress]) => progress.appId === app.id
            );

            return (
              <ApplicationCard
                key={app.id}
                application={app}
                projectId={projectId}
                deployments={deployments}
                versions={appVersions}
                uploads={appUploads}
                versionQuota={versionQuota}
                disabled={!isConnected}
                onAddVersion={() => setAddVersionApp(app)}
                onPreview={() => handlePreview(app)}
                onDeleteApplication={() => handleDeleteApplication(app)}
                onChangeThumbnail={() => setThumbnailApp(app)}
                onMoveApplication={() => setMoveApp(app)}
                onSetDefault={(versionId) => handleSetDefaultVersion(app.id, versionId)}
                onReplaceVersion={(versionId) => setReplaceVersion({ app, versionId })}
                onDeleteVersion={handleDeleteVersion}
                onPauseUpload={handlePauseUpload}
                onResumeUpload={handleResumeUpload}
                onCancelUpload={handleCancelUpload}
              />
            );
          })}
        </div>
      )}

      {/* Create Application Dialog */}
      {showCreateApplication && (
        <CreateApplicationDialog
          projectId={projectId}
          onClose={() => setShowCreateApplication(false)}
          onUploadStarted={handleUploadStarted}
        />
      )}

      {/* Add Version Dialog */}
      {addVersionApp && (
        <AddVersionDialog
          application={addVersionApp}
          onClose={() => setAddVersionApp(null)}
          onUploadStarted={handleUploadStarted}
        />
      )}

      {/* Replace Version Dialog */}
      {replaceVersion && (
        <AddVersionDialog
          application={replaceVersion.app}
          onClose={() => setReplaceVersion(null)}
          onUploadStarted={handleUploadStarted}
          replaceVersionId={replaceVersion.versionId}
        />
      )}

      {thumbnailApp && (
        <ChangeThumbnailDialog
          isOpen={true}
          application={thumbnailApp}
          onClose={() => setThumbnailApp(null)}
        />
      )}

      {moveApp && (
        <MoveApplicationDialog
          application={moveApp}
          onClose={() => setMoveApp(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmText="Delete"
        requireConfirmation={confirmDialog.requireConfirmation}
        confirmationPhrase={confirmDialog.confirmationPhrase}
      />
    </div>
  );
}
